// Compact round result — one calm calculation line between dice and selector.
import type { I18n } from '@aerofruits/i18n';
import { formatMultiplierTenths } from '../game/lightning-types.js';
import { buildResultCalculationLine, buildResultMoneySubline } from '../game/result-format.js';

export interface ResultBlockBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export class ResultBlock {
  private readonly root: HTMLElement;
  private readonly calculationLine: HTMLElement;
  private readonly moneyLine: HTMLElement;
  private readonly live: HTMLElement;
  private readonly i18n: I18n;
  private readonly formatMoney: (minor: number, currency: string) => string;
  private lastWinParams: Parameters<ResultBlock['showWin']>[0] | null = null;

  constructor(
    root: HTMLElement,
    opts: {
      i18n: I18n;
      formatMoney: (minor: number, currency: string) => string;
    },
  ) {
    this.root = root;
    this.i18n = opts.i18n;
    this.formatMoney = opts.formatMoney;

    root.classList.add('lightning-result');
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    root.setAttribute('aria-atomic', 'true');
    root.innerHTML = `
      <p class="lightning-result__calc" id="lightning-result-calc"></p>
      <p class="lightning-result__money" id="lightning-result-money" hidden></p>
      <div class="sr-only" id="lightning-result-live" aria-live="polite" aria-atomic="true"></div>`;

    this.calculationLine = root.querySelector('#lightning-result-calc')!;
    this.moneyLine = root.querySelector('#lightning-result-money')!;
    this.live = root.querySelector('#lightning-result-live')!;
    this.hide();
  }

  setBounds(bounds: ResultBlockBounds): void {
    this.root.style.left = `${bounds.left}px`;
    this.root.style.top = `${bounds.top}px`;
    this.root.style.width = `${bounds.width}px`;
    this.root.style.height = `${bounds.height}px`;
    this.updateMoneyVisibility(bounds.width, bounds.height);
  }

  getLayoutRect(): { width: number; height: number; top: number; left: number } {
    const r = this.root.getBoundingClientRect();
    return {
      left: r.left,
      top: r.top,
      width: r.width || parseFloat(this.root.style.width) || this.root.offsetWidth,
      height: r.height || parseFloat(this.root.style.height) || this.root.offsetHeight,
    };
  }

  applyTranslations(): void {
    if (this.root.dataset.state === 'hint') this.showHint();
    else if (this.lastWinParams) this.showWin(this.lastWinParams);
    else if (this.root.dataset.state === 'loss') this.showLoss();
  }

  hide(): void {
    this.root.dataset.state = 'hidden';
    this.root.setAttribute('hidden', '');
    this.calculationLine.textContent = '';
    this.moneyLine.textContent = '';
    this.moneyLine.setAttribute('hidden', '');
    this.live.textContent = '';
    this.lastWinParams = null;
  }

  showHint(): void {
    const t = this.i18n.t.bind(this.i18n);
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'hint';
    this.calculationLine.textContent = t('lnd.status.selectNumber');
    this.moneyLine.textContent = '';
    this.moneyLine.setAttribute('hidden', '');
    this.root.classList.remove('lightning-result--rolling', 'lightning-result--dim');
    this.lastWinParams = null;
  }

  prepareRoll(): void {
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'rolling';
    this.root.classList.add('lightning-result--rolling', 'lightning-result--dim');
    this.calculationLine.textContent = '';
    this.moneyLine.textContent = '';
    this.moneyLine.setAttribute('hidden', '');
    this.live.textContent = '';
    this.lastWinParams = null;
  }

  showLoss(): void {
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'loss';
    this.root.classList.remove('lightning-result--rolling', 'lightning-result--dim');
    this.calculationLine.textContent = buildResultCalculationLine({
      matchCount: 0,
      baseProfitMultiplierTenths: 0,
      lightningProfitMultiplierTenths: 0,
      stakeReturnMultiplierTenths: 0,
      totalPayoutMultiplierTenths: 0,
    });
    this.moneyLine.textContent = '';
    this.moneyLine.setAttribute('hidden', '');
    this.announceAll();
    this.lastWinParams = null;
  }

  showWin(params: {
    matchCount: number;
    baseProfitMultiplierTenths: number;
    lightningProfitMultiplierTenths: number;
    profitMultiplierTenths: number;
    profitAmountMinor: number;
    stakeReturnMultiplierTenths: number;
    stakeReturnAmountMinor: number;
    totalPayoutMultiplierTenths: number;
    totalPayoutAmountMinor: number;
    currency: string;
  }): void {
    this.lastWinParams = params;
    this.root.removeAttribute('hidden');
    this.root.dataset.state = 'complete';
    this.root.classList.remove('lightning-result--rolling', 'lightning-result--dim');
    this.calculationLine.innerHTML = this.renderCalculationHtml({
      matchCount: params.matchCount,
      baseProfitMultiplierTenths: params.baseProfitMultiplierTenths,
      lightningProfitMultiplierTenths: params.lightningProfitMultiplierTenths,
      stakeReturnMultiplierTenths: params.stakeReturnMultiplierTenths,
      totalPayoutMultiplierTenths: params.totalPayoutMultiplierTenths,
    });
    this.moneyLine.textContent = buildResultMoneySubline({
      profitAmountMinor: params.profitAmountMinor,
      totalPayoutAmountMinor: params.totalPayoutAmountMinor,
      currency: params.currency,
      formatMoney: this.formatMoney,
    });
    const boundsW = parseFloat(this.root.style.width) || this.root.offsetWidth;
    const boundsH = parseFloat(this.root.style.height) || this.root.offsetHeight;
    this.updateMoneyVisibility(boundsW, boundsH);
    this.announceAll();
  }

  private renderCalculationHtml(input: {
    matchCount: number;
    baseProfitMultiplierTenths: number;
    lightningProfitMultiplierTenths: number;
    stakeReturnMultiplierTenths: number;
    totalPayoutMultiplierTenths: number;
  }): string {
    const total = formatMultiplierTenths(input.totalPayoutMultiplierTenths);
    if (input.matchCount === 0) {
      return `<span class="lightning-result__muted">No matches</span> <span class="lightning-result__sep">·</span> <span class="lightning-result__total">Total ${total}</span>`;
    }
    const parts: string[] = [];
    if (input.baseProfitMultiplierTenths > 0) {
      parts.push(
        `<span class="lightning-result__base">Base ${formatMultiplierTenths(input.baseProfitMultiplierTenths)}</span>`,
      );
    }
    if (input.lightningProfitMultiplierTenths > 0) {
      parts.push(
        `<span class="lightning-result__lightning">Lightning ${formatMultiplierTenths(input.lightningProfitMultiplierTenths)}</span>`,
      );
    }
    if (input.stakeReturnMultiplierTenths > 0) {
      parts.push(
        `<span class="lightning-result__stake">Stake ${formatMultiplierTenths(input.stakeReturnMultiplierTenths)}</span>`,
      );
    }
    const sep = '<span class="lightning-result__sep"> + </span>';
    const eq = `<span class="lightning-result__sep"> = </span><span class="lightning-result__total">Total ${total}</span>`;
    return `${parts.join(sep)}${eq}`;
  }

  private updateMoneyVisibility(width: number, height: number): void {
    const showMoney =
      this.root.dataset.state === 'complete' &&
      width >= 280 &&
      height >= 52 &&
      this.moneyLine.textContent.length > 0;
    if (showMoney) this.moneyLine.removeAttribute('hidden');
    else this.moneyLine.setAttribute('hidden', '');
  }

  private announceAll(): void {
    const calcText = this.calculationLine.textContent ?? '';
    const moneyText = this.moneyLine.hasAttribute('hidden') ? '' : this.moneyLine.textContent ?? '';
    this.live.textContent = [calcText, moneyText].filter(Boolean).join('. ');
  }
}
